
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Music, MessageSquare, TrendingUp, Users, Zap, Shield, Award, Target } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ForArtists() {
  const navigate = useNavigate();

  useEffect(() => {
    // Add comprehensive SEO meta tags
    document.title = "For Independent Artists - Soundope 2025 | Free Feedback, Real Playlists";
    
    const metaTags = [
      { type: 'name', key: 'description', content: "Free feedback forever. Free playlist submissions. Paid from $1. No bots, no fake guarantees. Real curators, real taste, real results." },
      { type: 'name', key: 'keywords', content: "independent artist promotion, free music feedback, cheap playlist submission, soundope vs submithub, real curators" },
      { type: 'name', key: 'robots', content: 'index, follow' },
      
      // Open Graph
      { type: 'property', key: 'og:title', content: 'For Independent Artists - Soundope 2025' },
      { type: 'property', key: 'og:description', content: 'Free feedback, free playlists, paid from $1. No bots, no fake streams.' },
      { type: 'property', key: 'og:type', content: 'website' },
      { type: 'property', key: 'og:url', content: window.location.href },
      
      // Twitter
      { type: 'name', key: 'twitter:card', content: 'summary_large_image' },
      { type: 'name', key: 'twitter:title', content: 'For Independent Artists - Soundope' },
      { type: 'name', key: 'twitter:description', content: 'Real feedback, real playlists, real results' },
      
      // Canonical URL
      { type: 'link', key: 'canonical', content: window.location.origin + window.location.pathname },
    ];

    metaTags.forEach(({ type, key, content }) => {
      if (type === 'link') {
        let tag = document.querySelector(`link[rel="${key}"]`);
        if (tag) {
          tag.setAttribute('href', content);
        } else {
          tag = document.createElement('link');
          tag.setAttribute('rel', key);
          tag.setAttribute('href', content);
          document.head.appendChild(tag);
        }
      } else {
        // For meta tags, distinguish between 'name' and 'property' attributes
        const selector = type === 'property' ? `meta[property="${key}"]` : `meta[name="${key}"]`;
        let tag = document.querySelector(selector);
        
        if (tag) {
          tag.setAttribute('content', content);
        } else {
          tag = document.createElement('meta');
          tag.setAttribute(type, key);
          tag.setAttribute('content', content);
          document.head.appendChild(tag);
        }
      }
    });
  }, []);

  return (
    <div className="min-h-screen px-4 py-16 pb-32">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-5xl font-light mb-8 text-[#d0d0d0]">
            The Problem with Traditional Music Promotion
          </h1>
          <p className="text-lg md:text-xl text-[#909090] mb-4">
            (And why Soundope actually fixes it)
          </p>
        </div>

        {/* Comparison Table */}
        <div className="neuro-base rounded-3xl p-6 md:p-8 mb-16 overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#2a2a2a]">
                <th className="pb-4 pr-4 text-sm md:text-base font-medium text-[#b09090]">❌ The Old Way</th>
                <th className="pb-4 text-sm md:text-base font-medium text-[#90b090]">✓ Soundope – 2025 Reality</th>
              </tr>
            </thead>
            <tbody className="text-sm md:text-base">
              <tr className="border-b border-[#1a1a1a]">
                <td className="py-4 pr-4 text-[#808080]">
                  SubmitHub, Groover, Playlist Push, Daily Playlists, Musosoup, SoundCampaign = $1–$350 per submission
                </td>
                <td className="py-4 text-[#909090]">
                  <strong className="text-[#b0b0b0]">Free in-house playlist submission</strong> + paid starts at <strong className="text-[#b0b0b0]">$1</strong>
                </td>
              </tr>
              <tr className="border-b border-[#1a1a1a]">
                <td className="py-4 pr-4 text-[#808080]">
                  $50–$200 "guaranteed" packages that deliver bots and fake editorials
                </td>
                <td className="py-4 text-[#909090]">
                  <strong className="text-[#b0b0b0]">Zero bots. Zero canvas farms. Zero fake "Release Radar"</strong>
                </td>
              </tr>
              <tr className="border-b border-[#1a1a1a]">
                <td className="py-4 pr-4 text-[#808080]">
                  Feedback costs money (or you get generic AI slop)
                </td>
                <td className="py-4 text-[#909090]">
                  <strong className="text-[#b0b0b0]">100% free, brutally honest feedback</strong> from real humans who must listen to 75%+ of your track
                </td>
              </tr>
              <tr>
                <td className="py-4 pr-4 text-[#808080]">
                  One-way street – submit, pray, disappear
                </td>
                <td className="py-4 text-[#909090]">
                  Upload once → lands on Discovery → real people play + praise/scathe + optional paid playlist push
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* What Actually Happens */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-4xl font-light text-center text-[#d0d0d0] mb-12">
            What Actually Happens When You Upload
          </h2>

          <div className="space-y-6">
            <div className="neuro-base rounded-3xl p-6 md:p-8">
              <h3 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                1. Your track hits the Discovery page instantly
              </h3>
              <p className="text-base text-[#909090] leading-relaxed">
                Thousands of real listeners (no bots) see it, press play, and leave praise or scathe.
              </p>
            </div>

            <div className="neuro-base rounded-3xl p-6 md:p-8">
              <h3 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                2. Free feedback from actual humans – always
              </h3>
              <p className="text-base text-[#909090] leading-relaxed">
                Every review is from a real person who listened to at least 75% of the song. No AI, no "sounds good 🔥" spam.
              </p>
            </div>

            <div className="neuro-base rounded-3xl p-6 md:p-8">
              <h3 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                3. Free submission to our in-house playlists
              </h3>
              <p className="text-base text-[#909090] leading-relaxed">
                Zero cost. Real curators decide. If they love it, you're in.
              </p>
            </div>

            <div className="neuro-base rounded-3xl p-6 md:p-8">
              <h3 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                4. Want faster / bigger playlists? Pay the lowest prices in the game
              </h3>
              <p className="text-base text-[#909090] leading-relaxed mb-3">
                $1–$4 per submission (simple $1–$4 tiers)
              </p>
              <p className="text-sm text-[#808080]">
                Cheaper than SubmitHub, Groover, Playlist Push, Daily Playlists – with real taste attached.
              </p>
            </div>

          </div>
        </div>

        {/* Why Artists Choose Soundope */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-16">
          <h2 className="text-2xl md:text-3xl font-light text-center text-[#d0d0d0] mb-8">
            Why Artists Actually Choose Soundope in 2025
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-3 text-base md:text-lg text-[#909090]">
            <p>• Free feedback forever (real humans, no AI)</p>
            <p>• Free playlist submission (in-house playlists)</p>
            <p>• Paid playlist submission from $1 (lowest price anywhere)</p>
            <p>• No bots, no fake guarantees, no $50 monkey spank</p>
            <p>• Discovery page full of real listeners who actually press play</p>
            <p>• Praise / scathe system that tells you the truth</p>
            <p>• We love niche genres first (trap metal, phonk, screamo, crunkcore, witch house, hyperpop, blackgaze…) but all genres welcome</p>
          </div>
        </div>

        {/* Ready Section */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-light text-[#d0d0d0] mb-8">
            Ready?
          </h2>
        </div>

        {/* CTA Button */}
        <div className="text-center mb-8">
          <Button
            onClick={() => navigate(createPageUrl("Upload"))}
            className="neuro-base active:neuro-pressed rounded-3xl px-12 py-8 text-lg md:text-xl smooth-transition hover:scale-105"
            tooltip="Upload your track to get free feedback and free playlist submissions"
          >
            <span className="text-[#b0b0b0]">Upload Track – Free Feedback + Free Playlist Submission</span>
          </Button>
        </div>

        {/* Closing */}
        <p className="text-center text-base md:text-lg text-[#808080]">
          Soundope. BY artists, FOR artists.
        </p>
      </div>
    </div>
  );
}
